import { useEffect, useState } from "react";

// Styles
import MorningMeetingTopicClassifierNotesDashboardContainer from "./MorningMeetingTopicClassifierNotesDashboard.styles";

// Font Awesome
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

// Utils
import * as Database from "utils/database.utils";

// Components
import Button from "components/Common/Button/Button";
import LoadingSpinner from "components/Common/LoadingSpinner/LoadingSpinner";
import PageTitle from "components/Common/PageTitle/PageTitle";
import SearchOptions from "components/Common/SearchOptions/SearchOptions";
import MorningMeetingCompanyNotes from "../MorningMeetingCompanyNotes/MorningMeetingCompanyNotes";

// React Router
import { useHistory, useLocation } from "react-router-dom";

// Context
import { useBQMQ } from "context/BQMQ.context";
import {
    AnalystType,
    NoteType,
} from "./MorningMeetingTopicClassifierNotesDashboard.types";
import { GroupByEnum } from "../MorningMeetingNotes/MorningMeetingNotes";

const MorningMeetingTopicClassifierNotesDashboard = () => {
    const history = useHistory();
    const location = useLocation();
    const { analysts } = useBQMQ();

    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [analystsList, setAnalystsList] = useState<any>();
    const [filterQuery, setFilterQuery] = useState<any>();
    const [notes, setNotes] = useState<NoteType[]>([]);

    const [notFound, setNotFound] = useState(false);

    const filterOptions = [
        {
            id: 1,
            name: "Analyst",
            value: "analyst",
            type: "DROPDOWN",
            searchable: true,
            options: analystsList,
        },
        {
            id: 2,
            name: "Date Range",
            value: "dateRange",
            type: "DATE_RANGE",
        },
    ];

    const getMorningMeetingMinutes = async (queryString?: string) => {
        setNotFound(false);

        const baseUrl = `api/bqmq_voting/get-morning-meeting-minutes-by-topic-classifier/${id}`;
        const url = queryString
            ? `${baseUrl}?${encodeURI(queryString)}`
            : baseUrl;

        const response = await Database.get(url);
        if (response.ok) {
            const data = await response.json();
            setNotes(data.data);
        } else {
            setNotFound(true);
        }
    };

    const getAnalysts = (data: AnalystType[]) => {
        const uniqueAnalystsMap = new Map();

        data.filter((analyst: AnalystType) => analyst.active).forEach(
            (analyst: AnalystType) => {
                if (analyst.label) {
                    uniqueAnalystsMap.set(analyst.label, analyst.value);
                }
            }
        );

        const sortedUniqueAnalysts = Array.from(uniqueAnalystsMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([label, value]) => ({
                label,
                value,
            }));

        setAnalystsList(sortedUniqueAnalysts);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        setId(urlParams.get("id")!!);
        setName(urlParams.get("name")!!);
    }, []);

    useEffect(() => {
        // Wait for id to be parsed from URL and filter component to initialize to empty string
        if (!id || filterQuery === undefined) return;

        getMorningMeetingMinutes(filterQuery);
    }, [filterQuery, id]);

    useEffect(() => {
        getAnalysts(analysts);
    }, [analysts]);

    return (
        <MorningMeetingTopicClassifierNotesDashboardContainer className="pageContainer">
            <div style={{ display: "flex" }}>
                <h1 className="pageTitle">MORNING MEETING NOTES</h1>
                <Button
                    className="backButton"
                    text="BACK"
                    click={() =>
                        history.push(
                            `/company/morning-meeting-notes?groupBy=${GroupByEnum.BY_TOPIC_CLASSIFIER}`
                        )
                    }
                    icon={faArrowLeft}
                />
            </div>
            <hr />
            {id !== "" && !notFound ? (
                <>
                    <PageTitle
                        title={
                            <div className="resultsTitle">
                                <h1>{name}</h1>
                            </div>
                        }
                    />

                    <div className={"item-container"}>
                        <SearchOptions
                            setFilterQuery={setFilterQuery}
                            options={filterOptions}
                            noDebounce
                            useSimpleDates={true}
                        />
                    </div>

                    <div className={"morning-notes"}>
                        <MorningMeetingCompanyNotes
                            notes={notes}
                            setNotes={setNotes}
                            backPath={location.pathname + location.search}
                            backLabel={
                                "Back to " + name.toLowerCase() + " notes"
                            }
                        />
                    </div>
                </>
            ) : !notFound ? (
                <div className={"loadingScreen"}>
                    <LoadingSpinner />
                </div>
            ) : null}
            {notFound && (
                <div className="loadingScreen">
                    <h3 style={{ marginTop: "20px" }}>
                        No morning meeting notes found for {name}
                    </h3>
                </div>
            )}
        </MorningMeetingTopicClassifierNotesDashboardContainer>
    );
};

export default MorningMeetingTopicClassifierNotesDashboard;
